import {
  QButton,
  QIcon,
  QMenuItem,
  useCurrentUser,
} from '@qualio/ui-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useCopilotAccess } from '../../../../../hooks/useCopilotAccess';
import { DocumentOverviewContext } from '../../Context';
import { shouldRenderReviewButton } from '../../RenderLogic';

type ReviewButtonProps = {
  renderAs?: 'button' | 'menuItem';
};

export function ReviewButton({
  renderAs = 'button',
}: ReviewButtonProps = {}): JSX.Element | null {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { qualioDocument } = useContext(DocumentOverviewContext);
  const { copilotReviewDocument } = useFlags();
  const hasCopilotAccess = useCopilotAccess();

  if (
    !copilotReviewDocument ||
    !hasCopilotAccess ||
    !shouldRenderReviewButton(qualioDocument, currentUser)
  ) {
    return null;
  }

  const documentCode = qualioDocument.code;
  const title = qualioDocument.title;
  const version = `${qualioDocument.major_version}.${qualioDocument.minor_version}`;
  const label = t('buttons.review');

  const handleClick = (): void => {
    window.dispatchEvent(
      new CustomEvent('ShowAssistant', {
        detail: {
          stateType: 'prompt',
          initialPrompt: t('documentActions.reviewDocumentPrompt', {
            documentCode,
            title,
            version,
          }),
          autoSend: true,
        },
      }),
    );
  };

  if (renderAs === 'menuItem') {
    return (
      <QMenuItem
        icon={<QIcon iconName="QualioAI" />}
        data-cy="review-ai-menu-item"
        onClick={handleClick}
        fontSize="sm"
      >
        {label}
      </QMenuItem>
    );
  }

  return (
    <QButton
      variant="ghost"
      leftIcon="QualioAI"
      data-cy="review-ai-button"
      onClick={handleClick}
    >
      {label}
    </QButton>
  );
}
